import { LEGACY_VAT_RATE, round2 } from './vat';
import { bundleBreakdown, BreakdownRow, discountOnHighestRate, pickUnits, vatParts } from './bundles';
import { bonusTotals, commissionFor, countsForBonus, itemCommissionOf } from './bonus';

import { labelLinkFor } from './labelLink';
import { Order, OrderItem, User, ClassGroup, Product, OrderBundle, BundleStatus, UserRole, FinancialSummary } from '../types';

// --- FILTERING ---

/** Returns only active (non-deleted) orders */
export const getActiveOrders = (orders: Order[]): Order[] => {
    return orders.filter(o => !o.deletedAt);
};

type StatusFields = Pick<Order, 'status' | 'paymentMethod' | 'paymentStatus'>;

/**
 * Betald i kassan: pengarna finns redan hos CoffeeNova. Varken säljaren eller
 * den ansvariga har något att göra med ordern, och en hemleverans kan skickas
 * direkt. Avräkningen följer ändå gruppens faktura för perioden.
 */
export const isCheckoutPaid = (o: Pick<Order, 'paymentMethod' | 'paymentStatus'>): boolean =>
    o.paymentMethod === 'STRIPE' && o.paymentStatus === 'PAID';

/** Väntar på att den ansvariga skickar in: ordrar där säljarna tagit betalt. Kassaköp följer med automatiskt. */
export const awaitsSubmit = (o: StatusFields): boolean => o.status === 'PENDING' && !isCheckoutPaid(o);

/** Statusen som visas. Ett betalt kassaköp som inte avräknats än är "Klar", inte "Ej inskickad". */
export const displayStatus = (o: StatusFields): string =>
    o.status === 'PENDING' && isCheckoutPaid(o) ? 'READY' : (o.status || 'PENDING');

/** Ordrar som hämtas hos säljaren: ingår i leveransen till gruppen, plocklistan och etiketterna. */
export const pickupOrders = (orders: Order[]): Order[] => orders.filter(o => o.deliveryMethod !== 'HOME');

/** Hemleveranser: betalda i kassan och skickade av CoffeeNova direkt till kunden. */
export const homeOrders = (orders: Order[]): Order[] => orders.filter(o => o.deliveryMethod === 'HOME');

/** Returns only soft-deleted orders */
export const getDeletedOrders = (orders: Order[]): Order[] => {
    return orders.filter(o => o.deletedAt).sort((a,b) => (b.deletedAt || 0) - (a.deletedAt || 0));
};

// --- BUNDLING (Grouping by Class) ---

export const groupOrdersByClass = (
    orders: Order[],
    user: User, 
    classes: ClassGroup[],
    allUsers: User[] = [] // Optional: Only needed for Admin to look up teachers
): OrderBundle[] => {
    const grouped: Record<string, OrderBundle> = {};
    const unknownClassId = 'unknown';
    const isAdmin = user.role === UserRole.ADMIN;

    if (orders.length === 0) return [];

    orders.forEach(order => {
        let classId = unknownClassId;
        let className = 'Okänd Klass';
        let teacherName = 'Ingen';
        let deliveryAddress = '';

        // Admin Logic: Resolve class via student relationship OR snapshot fallback
        if (isAdmin) {
            const student = allUsers.find(u => u.id === order.studentId);
            
            if (student && student.classId) {
                // Happy path: Active student found
                classId = student.classId;
                const classObj = classes.find(c => c.id === classId);
                if (classObj) {
                    className = classObj.name;
                    deliveryAddress = classObj.deliveryAddress || '';
                }
            } else {
                // Fallback path: Student archived or missing. 
                const snapshotName = order.classNameSnapshot;
                if (snapshotName) {
                    const classMatch = classes.find(c => c.name === snapshotName);
                    if (classMatch) {
                        classId = classMatch.id;
                        className = classMatch.name;
                        deliveryAddress = classMatch.deliveryAddress || '';
                    } else {
                        className = snapshotName + ' (Arkiverad)';
                        classId = 'archived-' + snapshotName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
                    }
                }
            }

            if (classId !== unknownClassId && !classId.startsWith('archived-')) {
                const teacher = allUsers.find(u => u.role === UserRole.TEACHER && u.assignedClassId === classId);
                teacherName = teacher ? teacher.name : '-';
            }
        } 
        else {
            classId = user.assignedClassId || 'my-class';
            const myClass = classes.find(c => c.id === user.assignedClassId);
            className = myClass?.name || 'Min Grupp';
            deliveryAddress = myClass?.deliveryAddress || '';
            teacherName = user.name;
        }
        
        if (!grouped[classId]) {
            grouped[classId] = {
                classId,
                className,
                teacherName,
                deliveryAddress,
                orderCount: 0,
                totalAmount: 0,
                status: 'PENDING',
                orders: [],
                latestDate: 0,
                invoiceId: undefined
            };
        }

        grouped[classId].orders.push(order);
        grouped[classId].orderCount++;
        grouped[classId].totalAmount += order.totalAmount;
        if (order.timestamp > grouped[classId].latestDate) {
            grouped[classId].latestDate = order.timestamp;
        }
        
        // Populate Invoice ID if found (assumes bundle shares same invoice)
        if (order.invoiceId && !grouped[classId].invoiceId) {
            grouped[classId].invoiceId = order.invoiceId;
        }
    });

    return Object.values(grouped).map(b => {
        // Betalda kassaköp väntar inte på inskick och gör inte gruppen "ej inskickad".
        const statuses = new Set(b.orders.filter(o => o.status !== 'PENDING' || awaitsSubmit(o)).map(o => o.status));
        if (statuses.has('SUBMITTED')) b.status = 'SUBMITTED';
        else if (statuses.has('PENDING')) b.status = 'PENDING';
        else if (statuses.has('CONFIRMED')) b.status = 'CONFIRMED';
        else if (statuses.has('DELIVERED')) b.status = 'DELIVERED';
        else if (statuses.size === 0) b.status = 'CONFIRMED'; // bara betalda kassaköp: inget att skicka in
        else b.status = 'MIXED';
        return b;
    }).sort((a, b) => {
        if (a.status === 'SUBMITTED' && b.status !== 'SUBMITTED') return -1;
        if (a.status !== 'SUBMITTED' && b.status === 'SUBMITTED') return 1;
        if (a.status === 'PENDING' && (b.status === 'CONFIRMED' || b.status === 'DELIVERED')) return -1;
        if ((a.status === 'CONFIRMED' || a.status === 'DELIVERED') && b.status === 'PENDING') return 1;
        return b.latestDate - a.latestDate;
    });
};

// --- FINANCIALS ---

export interface ItemFinancials {
    revenue: number;
    cost: number;
    classProfit: number;
}

/**
 * Vad en orderrad ger gruppen och bolaget. Gruppens del kommer från
 * bonussystemet (services/bonus.ts) och beror på hela periodens försäljning,
 * så den som anropar skickar in provisionen per rad (commissionOf).
 * Inköpspriset tas från ögonblicksbilden på raden när den finns.
 * CoffeeNovas vinst (utan moms, med frakt och kortavgifter) räknas i
 * services/companyProfit.ts, inte här.
 */
export const calculateItemFinancials = (item: OrderItem, products: Product[], commissionOf: (item: OrderItem) => number): ItemFinancials => {
    const qty = item.quantity;
    const revenue = item.priceAtMoment * qty;
    const classProfit = commissionOf(item);
    let cost: number;
    if (item.purchasePriceSnapshot !== undefined) {
        cost = item.purchasePriceSnapshot * qty;
    } else {
        const product = products.find(p => p.id === item.productId);
        const variant = item.variantId ? product?.variants?.find(v => v.id === item.variantId) : undefined;
        cost = (variant?.purchasePrice ?? product?.purchasePrice ?? 0) * qty;
    }
    return { revenue, cost, classProfit };
};

/** Summan av ordrarnas varor (utan frakt). Den enda summeringen av orderbelopp. */
export const sumGoods = (orders: Order[]): number =>
    Math.round(orders.reduce((sum, o) => sum + o.totalAmount, 0) * 100) / 100;

export interface PaymentSummary {
    /** Alla ordrars varor. */
    total: number;
    /** Ordrar som betalas till säljaren (direkt eller Swish). */
    pickupOrders: Order[];
    pickupTotal: number;
    /** Det säljarna fått in. */
    collected: number;
    unpaidCount: number;
    /** Köp betalda i kassan (Stripe): pengarna finns hos CoffeeNova. */
    onlineOrders: Order[];
    onlineTotal: number;
    homeCount: number;
    /** Betalt av kunderna totalt = insamlat hos säljarna + betalt i kassan. */
    paidByCustomers: number;
}

/** Betalningsläget för en grupp ordrar. Delas av alla vyer som visar "betalt". */
export const summarizePayments = (orders: Order[]): PaymentSummary => {
    const pickupOrders = orders.filter(o => o.paymentMethod !== 'STRIPE');
    const onlineOrders = orders.filter(o => o.paymentMethod === 'STRIPE');
    const collected = sumGoods(pickupOrders.filter(o => o.paymentStatus === 'PAID'));
    const onlineTotal = sumGoods(onlineOrders);
    return {
        total: sumGoods(orders),
        pickupOrders,
        pickupTotal: sumGoods(pickupOrders),
        collected,
        unpaidCount: pickupOrders.filter(o => o.paymentStatus !== 'PAID').length,
        onlineOrders,
        onlineTotal,
        homeCount: onlineOrders.filter(o => o.deliveryMethod === 'HOME').length,
        paidByCustomers: Math.round((collected + onlineTotal) * 100) / 100,
    };
};

/** Vanliga produkter som redan fakturerats i gruppens period (fakturerade, ej borttagna ordrar). */
export const invoicedRegularSales = (classOrders: Order[]): number =>
    bonusTotals(classOrders.filter(o => o.invoiceId && countsForBonus(o))).regularSales;

/**
 * Gruppens ekonomi för en faktura. Gruppens provision räknas med
 * bonustrappan på allt den sålt (fortsätter från det som redan fakturerats
 * i perioden), men gruppen har bara pengarna för ordrarna som hämtats hos
 * säljarna; det kunderna betalat i kassan finns redan hos CoffeeNova:
 *
 *   att betala = hämtat hos säljarna − gruppens provision
 *
 * Blir det negativt betalar CoffeeNova ut mellanskillnaden (avräkning).
 * Frakten ligger utanför totalAmount och ger ingen provision.
 */
export const calculateFinancials = (orders: Order[], products: Product[], priorRegularSales = 0): FinancialSummary => {
    const bonus = commissionFor(orders, priorRegularSales);
    const commissionOf = itemCommissionOf(bonus);
    let totalSales = 0;
    let paidOnline = 0;
    let revenueMinusCost = 0;

    const payments = summarizePayments(orders);
    totalSales = payments.total;
    paidOnline = payments.onlineTotal;
    orders.forEach(order => {
        order.items.forEach(item => {
            const { revenue, cost } = calculateItemFinancials(item, products, commissionOf);
            revenueMinusCost += revenue - cost;
        });
    });

    const totalClassProfit = bonus.commission;
    const collectedBySellers = totalSales - paidOnline;
    return {
        totalSales,
        collectedBySellers,
        paidOnline,
        totalClassProfit,
        totalCompanyProfit: revenueMinusCost - totalClassProfit,
        toPay: Math.round((collectedBySellers - totalClassProfit) * 100) / 100,
        bonus,
        priorRegularSales,
    };
};

export const aggregateProducts = (orders: Order[], products: Product[], rateOf: (item: { productId: string; vatRate?: number | null }) => number = i => i.vatRate ?? LEGACY_VAT_RATE) => {
    const map = new Map<string, {
        id: string; // unique key for list rendering
        productId: string;
        name: string;
        variantName?: string;
        totalQty: number;
        revenue: number;
        price: number;
        vatRate: number;
        /** Paket: delarna med denna sats, med ordinarie pris och andel av paketpriset (per paket). */
        parts?: BreakdownRow[];
        /** Paket: hela paketets rabatt per paket (också när paketet delats på flera satser). */
        bundleDiscount?: number;
        /** Paketet har delar med olika momssatser och står på en rad per sats. */
        mixedBundle?: boolean;
        /** Rabatten är dragen på produkten med högst moms (annars efter ordinarie pris). */
        discountOnHighestRate?: boolean;
    }>();

    orders.forEach(o => {
        o.items.forEach(i => {
            // Paket med flera satser blir en rad per sats med delarnas andel av priset.
            const parts = vatParts(i, rateOf);
            const byRate = new Map<number, number>();
            parts.forEach(p => byRate.set(p.rate, (byRate.get(p.rate) || 0) + p.amount));
            const mixed = byRate.size > 1;
            byRate.forEach((amount, vatRate) => {
                // Momssatsen ingår i nyckeln: ändras satsen under säljperioden blir det två rader.
                const key = `${i.productId}_${i.variantId || 'default'}_${vatRate}`;

                if (!map.has(key)) {
                    const p = products.find(prod => prod.id === i.productId);
                    // Paket: uppdelningen för fakturans specifikation (samma siffror som kvittot).
                    const all = i.components || [];
                    const comps = all.filter(c => rateOf(c) === vatRate);
                    const complete = comps.length > 0 && all.every(c => Number.isFinite(c.unitPrice) && Number.isFinite(c.allocated));
                    const breakdown = complete
                        ? bundleBreakdown(comps.map(c => ({ ...c, vatRate })), i.priceAtMoment)
                        : null;
                    // Rabatten räknas på hela paketet: ordinarie pris för alla delar mot paketpriset.
                    const whole = complete ? bundleBreakdown(all, i.priceAtMoment) : null;
                    // Fallback to snapshot name if product deleted, or just generic
                    const base = p ? p.name : (i.productNameSnapshot || 'Produkt');
                    map.set(key, {
                        id: key,
                        productId: i.productId,
                        name: mixed ? `${base}, del med ${vatRate} % moms` : base,
                        variantName: i.variantName,
                        totalQty: 0,
                        revenue: 0,
                        price: i.quantity > 0 ? round2(amount / i.quantity) : i.priceAtMoment,
                        vatRate,
                        parts: breakdown?.rows,
                        bundleDiscount: whole?.discount,
                        mixedBundle: breakdown ? mixed : undefined,
                        discountOnHighestRate: whole ? discountOnHighestRate(whole.rows) : undefined,
                    });
                }

                const entry = map.get(key)!;
                entry.totalQty += i.quantity;
                entry.revenue += amount;
            });
        });
    });

    // Convert map to array and sort by product name
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/** Plocklistan: paket blir sina produkter, så att rätt antal påsar och flaskor plockas. */
export const aggregatePickUnits = (orders: Order[], products: Product[]) => {
    const nameOf = (id: string, snapshot?: string) => products.find(p => p.id === id)?.name || snapshot || 'Produkt';
    const map = new Map<string, { id: string; productId: string; name: string; variantName?: string; totalQty: number; inBundles: number }>();
    for (const o of orders) {
        for (const item of o.items) {
            for (const u of pickUnits(item, nameOf)) {
                const key = `${u.productId}_${u.variantId || 'default'}`;
                const entry = map.get(key) || { id: key, productId: u.productId, name: u.name, variantName: u.variantName, totalQty: 0, inBundles: 0 };
                entry.totalQty += u.quantity;
                if (u.bundleName) entry.inBundles += u.quantity;
                map.set(key, entry);
            }
        }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'sv'));
};

// --- PRINT HELPERS ---

/** Ordernumret kunden ser: de första 8 tecknen i id:t, som i orderbekräftelsen (api/orders/create-order.ts). */
export const shortOrderNumber = (id: string): string => id.replace(/-/g, '').slice(0, 8).toUpperCase();

export interface StickerData {
    productName: string; 
    variantName: string; 
    studentName: string; 
    customerName: string;
    customerAddress: string;
    customerPhone: string;
    orderId: string;
    timestamp: number;
    /** Dit etikettens QR-kod går: länken som angetts på produkten i admin, annars butiken. */
    labelUrl: string;
    /** Paketet påsen kommer ur, när den ingår i ett paket. */
    bundleName?: string;
}

export const flattenOrderItemsForLabels = (orders: Order[], products: Product[], users: User[]): StickerData[] => {
    const stickers: StickerData[] = [];

    const nameOf = (id: string, snapshot?: string) => products.find(p => p.id === id)?.name || snapshot || 'Produkt';
    orders.forEach(order => {
        // En etikett per påse: paket blir sina produkter, var och en med sin produkts länk.
        order.items.flatMap(item => pickUnits(item, nameOf)).forEach(unit => {
            const p = products.find(prod => prod.id === unit.productId);
            for (let i = 0; i < unit.quantity; i++) {
                stickers.push({
                    productName: unit.name,
                    variantName: unit.variantName || '',
                    studentName: order.studentName || '',
                    customerName: order.customerName,
                    customerAddress: order.customerAddress,
                    customerPhone: order.customerPhone,
                    // Samma ordernummer som kunden fick i orderbekräftelsen.
                    orderId: shortOrderNumber(order.id),
                    timestamp: order.timestamp,
                    labelUrl: labelLinkFor(p),
                    bundleName: unit.bundleName,
                });
            }
        });
    });
    
    return stickers;
};

// --- STATUS HELPERS ---

export const getOrderStatusLabel = (status: string) => {
    switch (status) {
        case 'PENDING': return 'Ej inskickad';
        case 'READY': return 'Klar';
        case 'SUBMITTED': return 'Inskickad';
        case 'CONFIRMED': return 'Bekräftad';
        case 'DELIVERED': return 'Levererad';
        case 'MIXED': return 'Blandad';
        default: return status;
    }
};

export const getOrderStatusType = (status: string): 'success' | 'warning' | 'info' | 'purple' | 'neutral' => {
    switch (status) {
        case 'CONFIRMED': return 'success';
        case 'READY': return 'success';
        case 'SUBMITTED': return 'purple';
        case 'DELIVERED': return 'info';
        case 'PENDING': return 'warning';
        default: return 'neutral';
    }
};

export const getOrderStatusColorClass = (status: string) => {
    switch (status) {
        case 'CONFIRMED':
        case 'READY': return 'bg-green-50 text-green-700 border-green-100';
        case 'SUBMITTED': return 'bg-purple-50 text-purple-700 border-purple-100';
        case 'DELIVERED': return 'bg-blue-50 text-blue-700 border-blue-100';
        default: return 'bg-yellow-50 text-yellow-700 border-yellow-100';
    }
};

/**
 * Vilka ordrar betalunderlaget ska visa. Underlaget gäller det gruppen ska
 * betala härnäst, så ordrar som redan ligger på en faktura räknas inte in —
 * annars ser det ut som att gruppen ska betala dem två gånger.
 */
export const ordersForPaymentSummary = (orders: Order[]) => {
    // Betalda kassaköp ingår utan inskick (pengarna finns redan hos CoffeeNova).
    const submitted = orders.filter(o => !awaitsSubmit(o) && !o.deletedAt);
    const notInvoiced = submitted.filter(o => !o.invoiceId);
    const invoiced = submitted.filter(o => !!o.invoiceId);
    if (notInvoiced.length === 0) {
        return { orders: submitted, alreadyInvoiced: null, allInvoiced: submitted.length > 0, priorRegularSales: 0 };
    }
    return {
        orders: notInvoiced,
        // Bonustrappan fortsätter från det som redan fakturerats i perioden.
        priorRegularSales: invoicedRegularSales(orders),
        alreadyInvoiced: invoiced.length > 0
            ? { count: invoiced.length, sales: invoiced.reduce((sum, o) => sum + o.totalAmount, 0) }
            : null,
        allInvoiced: false,
    };
};
